import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAudience, type AudienceDetail, type AudienceMetaPatch, type AudiencePerson } from "../../hooks/useAudience";
import { api } from "../../api/client";
import { DcAttachGame } from "./DcAttachGame";
import { DcPersonOrigin, ConsentMark } from "./DcPersonOrigin";
import { useAvatarSrc } from "../../hooks/useAvatarSrc";
import css from "./dcSections.module.css";
import { BroadcastPanel } from "./broadcast/BroadcastPanel"; /* 06.10: рассылка заданиями — журнал, ход, без дублей */

type SortKey = "coins" | "games" | "money" | "team" | "fresh";
type TagKey = "tag_minor" | "tag_adult" | "tag_competitor";
type AudienceGameComments = {
  dcComment: string | null;
  reports: {
    operator: string;
    comment: string | null;
    ooComment: string | null;
    needsCorrection: boolean;
    correctionReason: string | null;
    isPrimary: boolean;
  }[];
};

const SORT_CHIPS: { key: SortKey; label: string }[] = [
  { key: "fresh", label: "Свежие" },
  { key: "coins", label: "Коины" },
  { key: "games", label: "Игры" },
  { key: "money", label: "Деньги" },
  { key: "team", label: "Команды" },
];
const SORT_LABEL: Record<SortKey, string> = Object.fromEntries(SORT_CHIPS.map((s) => [s.key, s.label])) as Record<SortKey, string>;

const TAGS: { key: TagKey; label: string; short: string }[] = [
  { key: "tag_minor", label: "<18", short: "<18" },
  { key: "tag_adult", label: "18+", short: "18+" },
  { key: "tag_competitor", label: "Конкр", short: "Конкр" },
];

const TYPE_LABELS: Record<string, string> = {
  code: "Коды ТАЙНИКа",
  game: "За игры",
  game_played: "За игры",
  referral: "Рефералы",
  add_friend_to_game: "Друзья в игре",
  app_open: "Заходы друзей",
  review: "Отзывы",
  article: "Статьи",
  shop: "Покупки в Лавке",
  shop_refund: "Возвраты Лавки",
  bonus: "Бонусы",
};

const typeLabel = (t: string) => TYPE_LABELS[t] ?? t;
const money = (n: number) => Math.round(Number(n) || 0).toLocaleString("ru-RU");
const tagOn = (p: AudiencePerson, key: TagKey): boolean => Number(p[key]) === 1;

function fmtDate(s: string): string {
  if (!s) return "—";
  const d = new Date(s.replace(" ", "T") + "Z");
  if (Number.isNaN(d.getTime())) return s.slice(0, 10);
  const b = new Date(d.getTime() + 5 * 3600 * 1000);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(b.getUTCDate())}.${p(b.getUTCMonth() + 1)}.${String(b.getUTCFullYear()).slice(2)}`;
}

function tagsOf(p: AudiencePerson): string[] {
  return TAGS.filter((tag) => tagOn(p, tag.key)).map((tag) => tag.short);
}

function compareBy(key: SortKey, a: AudiencePerson, b: AudiencePerson): number {
  const byName = a.name.localeCompare(b.name, "ru");
  if (key === "coins") return b.uc_balance - a.uc_balance || b.booked - a.booked;
  if (key === "games") return b.games - a.games;
  if (key === "money") return b.money - a.money;
  if (key === "fresh") {
    const av = a.lastGame ? a.lastGame.date + " " + a.lastGame.time : "";
    const bv = b.lastGame ? b.lastGame.date + " " + b.lastGame.time : "";
    if (av !== bv) return bv.localeCompare(av); // свежее (позже дата+время) — выше
  }
  const at = a.teamId ? 0 : 1;
  const bt = b.teamId ? 0 : 1;
  if (at !== bt) return at - bt;
  if (a.teamId !== b.teamId) return (a.teamId ?? Number.MAX_SAFE_INTEGER) - (b.teamId ?? Number.MAX_SAFE_INTEGER);
  if (a.teamSize !== b.teamSize) return b.teamSize - a.teamSize;
  return byName;
}

export function DcAudienceSection({ onToast }: { onToast: (m: string) => void }) {
  const { people, loading, error, reload, patchMeta, checkBlocks } = useAudience();
  const [q, setQ] = useState("");
  const [sortKeys, setSortKeys] = useState<SortKey[]>(["money"]);
  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  const [showStaff, setShowStaff] = useState(false);
  const [groupTeams, setGroupTeams] = useState(false);
  const [showExcluded, setShowExcluded] = useState(false);
  const [tagFilter, setTagFilter] = useState<Set<TagKey>>(new Set());
  const [detail, setDetail] = useState<Record<number, AudienceDetail>>({});
  const [detailBusy, setDetailBusy] = useState<number | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [blocksBusy, setBlocksBusy] = useState(false);
  const primarySort = sortKeys[0] ?? "money";

  // Долгий тап по строке → режим выделения; не отпуская, тянешь вверх/вниз —
  // выделяется весь диапазон (как в галереях). Скролл отменяет long-press.
  const listRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ active: boolean; startTg: number; base: Set<number>; order: number[]; timer: number | null; sx: number; sy: number; suppressClick: boolean; pointerId: number }>(
    { active: false, startTg: 0, base: new Set(), order: [], timer: null, sx: 0, sy: 0, suppressClick: false, pointerId: -1 },
  );

  useEffect(() => {
    if (!sortOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!sortRef.current?.contains(e.target as Node)) setSortOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSortOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [sortOpen]);

  const toggleSortKey = (key: SortKey) => setSortKeys((prev) => {
    // Клик всегда делает ключ ГЛАВНЫМ (в начало), чтобы список видимо пересортировался.
    // Клик по текущему главному — убирает его (падаем на следующий), но не опустошаем.
    if (prev[0] === key) return prev.length === 1 ? prev : prev.slice(1);
    return [key, ...prev.filter((x) => x !== key)];
  });

  const view = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let list = needle ? people.filter((p) => (`${p.name} @${p.username} ${p.tg}`).toLowerCase().includes(needle)) : people;
    if (!showStaff) list = list.filter((p) => !p.staff);
    if (!showExcluded) list = list.filter((p) => !p.excluded);
    if (tagFilter.size) list = list.filter((p) => [...tagFilter].every((tag) => tagOn(p, tag)));
    return [...list].sort((a, b) => {
      for (const key of sortKeys) {
        const v = compareBy(key, a, b);
        if (v !== 0) return v;
      }
      return b.uc_balance - a.uc_balance || b.booked - a.booked || a.name.localeCompare(b.name, "ru");
    });
  }, [showStaff, people, q, showExcluded, sortKeys, tagFilter]);

  // Группировка по командам (тумблер): команда выводится блоком на позиции своего лучшего
  // по текущей сортировке участника, участники — рядом. displayList — плоский порядок КАК НА
  // ЭКРАНЕ, чтобы drag-выделение совпадало с визуальным порядком блоков.
  const { blocks, displayList } = useMemo(() => {
    type Leaf = { kind: "solo"; p: AudiencePerson } | { kind: "team"; teamId: number; members: AudiencePerson[] };
    type Block = Leaf | { kind: "relation"; relationId: number; children: Leaf[] };
    if (!groupTeams) return { blocks: view.map((p): Block => ({ kind: "solo", p })), displayList: view };
    const byTeam = new Map<number, AudiencePerson[]>();
    const byRel = new Map<number, AudiencePerson[]>();
    for (const p of view) {
      if (p.teamId != null) { const a = byTeam.get(p.teamId); if (a) a.push(p); else byTeam.set(p.teamId, [p]); }
      if (p.relationId != null) { const a = byRel.get(p.relationId); if (a) a.push(p); else byRel.set(p.relationId, [p]); }
    }
    const emittedTeam = new Set<number>();
    const emittedRel = new Set<number>();
    const blocks: Block[] = [];
    const displayList: AudiencePerson[] = [];
    // «Листья» кластера: co-play команды (рамка) + одиночки, в порядке сортировки.
    const leaves = (members: AudiencePerson[]): Leaf[] => {
      const out: Leaf[] = [];
      for (const m of members) {
        if (m.teamId == null) { out.push({ kind: "solo", p: m }); displayList.push(m); continue; }
        if (emittedTeam.has(m.teamId)) continue;
        emittedTeam.add(m.teamId);
        const tm = byTeam.get(m.teamId)!;
        if (tm.length < 2) { out.push({ kind: "solo", p: m }); displayList.push(m); }
        else { out.push({ kind: "team", teamId: m.teamId, members: tm }); displayList.push(...tm); }
      }
      return out;
    };
    for (const p of view) {
      if (p.relationId == null) { blocks.push({ kind: "solo", p }); displayList.push(p); continue; }
      if (emittedRel.has(p.relationId)) continue;
      emittedRel.add(p.relationId);
      const ch = leaves(byRel.get(p.relationId)!);
      if (ch.length === 1) blocks.push(ch[0]!); // кластер = одна команда без рефер-довесков → просто рамка
      else blocks.push({ kind: "relation", relationId: p.relationId, children: ch });
    }
    return { blocks, displayList };
  }, [view, groupTeams]);
  const viewRef = useRef(displayList);
  viewRef.current = displayList;
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  const tgFromPoint = (x: number, y: number): number | null => {
    const el = document.elementFromPoint(x, y) as HTMLElement | null;
    const host = el?.closest?.("[data-audtg]") as HTMLElement | null;
    const raw = host?.dataset?.audtg;
    return raw ? Number(raw) : null;
  };
  const applyDragRange = (endTg: number) => {
    const d = dragRef.current;
    const a = d.order.indexOf(d.startTg);
    const b = d.order.indexOf(endTg);
    if (a < 0 || b < 0) return;
    const [lo, hi] = a <= b ? [a, b] : [b, a];
    const next = new Set(d.base);
    for (let i = lo; i <= hi; i += 1) next.add(d.order[i]!);
    setSelected(next);
  };
  const cancelLongPress = () => {
    const d = dragRef.current;
    if (d.timer != null) { window.clearTimeout(d.timer); d.timer = null; }
  };
  const onListPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const tg = tgFromPoint(e.clientX, e.clientY);
    if (tg == null) return;
    const d = dragRef.current;
    d.sx = e.clientX;
    d.sy = e.clientY;
    d.startTg = tg;
    d.pointerId = e.pointerId;
    cancelLongPress();
    d.timer = window.setTimeout(() => {
      d.timer = null;
      d.active = true;
      d.suppressClick = true;
      d.base = new Set(selectedRef.current);
      d.order = viewRef.current.map((p) => p.tg);
      applyDragRange(tg);
      try { listRef.current?.setPointerCapture(d.pointerId); } catch { /* не критично */ }
      (navigator as Navigator & { vibrate?: (ms: number) => void }).vibrate?.(30);
    }, 420);
  };
  const onListPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (d.active) {
      const tg = tgFromPoint(e.clientX, e.clientY);
      if (tg != null) applyDragRange(tg);
      return;
    }
    if (d.timer != null && (Math.abs(e.clientX - d.sx) > 10 || Math.abs(e.clientY - d.sy) > 10)) cancelLongPress(); // это скролл
  };
  const onListPointerEnd = () => {
    cancelLongPress();
    dragRef.current.active = false; // suppressClick гасится в onClickCapture
  };
  const onListClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (dragRef.current.suppressClick) {
      e.preventDefault();
      e.stopPropagation();
      dragRef.current.suppressClick = false;
    }
  };
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    // non-passive: во время drag-выделения блокируем прокрутку страницы.
    const onTouchMove = (e: TouchEvent) => { if (dragRef.current.active) e.preventDefault(); };
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => el.removeEventListener("touchmove", onTouchMove);
  }, []);

  const totals = useMemo(() => ({
    coins: view.reduce((s, p) => s + Number(p.uc_balance || 0), 0),
    money: view.reduce((s, p) => s + Number(p.money || 0), 0),
    staff: people.filter((p) => p.staff).length,
    excluded: people.filter((p) => p.excluded).length,
  }), [people, view]);

  const toggle = (p: AudiencePerson) => {
    if (open === p.tg) { setOpen(null); return; }
    setOpen(p.tg);
    if (!detail[p.tg]) {
      setDetailBusy(p.tg);
      api<AudienceDetail>(`/api/audience/${p.tg}`)
        .then((d) => setDetail((prev) => ({ ...prev, [p.tg]: d })))
        .catch(() => onToast("Не удалось загрузить детали"))
        .finally(() => setDetailBusy(null));
    }
  };

  // После ручной привязки список игр в карточке должен обновиться сразу.
  const reloadDetail = useCallback((tg: number) => {
    api<AudienceDetail>(`/api/audience/${tg}`)
      .then((d) => setDetail((prev) => ({ ...prev, [tg]: d })))
      .catch(() => onToast("Не удалось обновить карточку"));
  }, [onToast]);

  const saveMeta = async (p: AudiencePerson, patch: AudienceMetaPatch) => {
    try {
      await patchMeta(p.tg, patch);
    } catch {
      onToast("Не удалось сохранить метку");
    }
  };

  const toggleTagFilter = (tag: TagKey) => setTagFilter((prev) => {
    const next = new Set(prev);
    next.has(tag) ? next.delete(tag) : next.add(tag);
    return next;
  });

  const toggleSel = (tg: number) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(tg) ? next.delete(tg) : next.add(tg);
    return next;
  });
  const selectAllVisible = () => setSelected(new Set(view.map((p) => p.tg)));
  const clearSel = () => setSelected(new Set());
  // Долгий тап по чипу «staff» при выделении → отметить выделенных как staff (мета-флаг).
  const staffPress = useRef<{ timer: number | null; fired: boolean }>({ timer: null, fired: false });
  const markSelectedStaff = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    await Promise.all(ids.map((tg) => { const pp = people.find((x) => x.tg === tg); return pp ? saveMeta(pp, { staff: 1 }) : Promise.resolve(); }));
    onToast(`В staff: ${ids.length}`);
    setSelected(new Set());
    reload();
  };
  const onStaffPointerDown = () => {
    staffPress.current.fired = false;
    if (selected.size === 0) return;
    staffPress.current.timer = window.setTimeout(() => {
      staffPress.current.timer = null;
      staffPress.current.fired = true;
      (navigator as Navigator & { vibrate?: (ms: number) => void }).vibrate?.(30);
      void markSelectedStaff();
    }, 550);
  };
  const onStaffPointerUp = () => {
    if (staffPress.current.timer != null) { window.clearTimeout(staffPress.current.timer); staffPress.current.timer = null; }
  };
  const onStaffClick = () => {
    if (staffPress.current.fired) { staffPress.current.fired = false; return; }
    setShowStaff((v) => !v);
  };
  const runCheckBlocks = async () => {
    setBlocksBusy(true);
    try { const r = await checkBlocks(); onToast(`Заблокировали бота: ${r.blocked} из ${r.checked}`); reload(); }
    catch { onToast("Не удалось проверить блоки"); }
    finally { setBlocksBusy(false); }
  };

  if (error) {
    return (
      <div className={css.main}>
        <div className={css.err}>Ошибка загрузки: {error}</div>
        <button type="button" className={css.audChip} onClick={reload}>Повторить</button>
      </div>
    );
  }
  if (loading) return <div className={css.main}><div className={css.loading}>Загрузка…</div></div>;

  return (
    <div className={css.main}>
      {broadcastOpen ? <BroadcastPanel selectedIds={[...selected]} onClose={() => setBroadcastOpen(false)} onSent={() => { setBroadcastOpen(false); setSelected(new Set()); }} onToast={onToast} /> : null}
      <div className={css.search}>
        <svg className={css.lens} viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="square" aria-hidden="true"><circle cx="6" cy="6" r="4" /><path d="M9 9l4 4" /></svg>
        <input className={css.searchInput} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Имя, @ник или tg id…" />
      </div>

      <div className={css.audToolbar}>
        <div className={css.audSortWrap} ref={sortRef}>
          <button type="button" className={`${css.sortChip} ${sortOpen ? css.sortChipOn : ""}`} onClick={() => setSortOpen((v) => !v)} aria-expanded={sortOpen}>
            {sortKeys.map((key) => SORT_LABEL[key]).join(" → ")}
          </button>
          {sortOpen ? (
            <div className={css.audSortMenu}>
              {SORT_CHIPS.map((s) => {
                const idx = sortKeys.indexOf(s.key);
                return (
                  <button key={s.key} type="button" className={idx >= 0 ? css.audSortItemOn : ""} onClick={() => toggleSortKey(s.key)}>
                    <span>{s.label}</span>{idx >= 0 ? <b>{idx + 1}</b> : null}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
        <button type="button" className={`${css.audChip} ${groupTeams ? css.audChipOn : ""}`} onClick={() => setGroupTeams((v) => !v)}>team</button>
        <button type="button" className={`${css.audChip} ${showStaff ? css.audChipOn : ""}`} onPointerDown={onStaffPointerDown} onPointerUp={onStaffPointerUp} onPointerLeave={onStaffPointerUp} onClick={onStaffClick} title="Долгий тап с выделением — внести в staff">staff <small>{totals.staff}</small></button>
        <button type="button" className={`${css.audChip} ${showExcluded ? css.audChipOn : ""}`} onClick={() => setShowExcluded((v) => !v)}>Искл. <small>{totals.excluded}</small></button>
        {TAGS.map((tag) => (
          <button key={tag.key} type="button" className={`${css.audChip} ${tagFilter.has(tag.key) ? css.audChipOn : ""}`} onClick={() => toggleTagFilter(tag.key)}>{tag.label}</button>
        ))}
        <button type="button" className={css.audChip} onClick={selectAllVisible}>Все <small>{view.length}</small></button>
        {selected.size > 0 ? <button type="button" className={css.audChip} onClick={clearSel}>Снять <small>{selected.size}</small></button> : null}
        <button type="button" className={`${css.audChip} ${selected.size ? css.audChipOn : ""}`} onClick={() => setBroadcastOpen(true)}>Рассылка{selected.size ? ` ${selected.size}` : ""}</button>
        <button type="button" className={css.audChip} onClick={runCheckBlocks} disabled={blocksBusy}>{blocksBusy ? "проверяю…" : "блоки"}</button>
      </div>

      <p className={css.audLegendLine}>Людей: <b>{view.length}</b>{view.length !== people.length ? ` из ${people.length}` : ""} · ₽ {money(totals.money)} · ☑ галочка в боте: <b>{view.filter((x) => Number(x.consent) === 1).length}</b> — ИГР сыграно · ₽ выручка · привёл · теги &lt;18 / 18+ / Конкр</p>

      {view.length === 0 ? <div className={css.empty}>Никого не найдено</div> : null}

      <div
        className={css.audList}
        ref={listRef}
        onPointerDown={onListPointerDown}
        onPointerMove={onListPointerMove}
        onPointerUp={onListPointerEnd}
        onPointerCancel={onListPointerEnd}
        onClickCapture={onListClickCapture}
      >
        {blocks.map((b) => {
          const row = (p: AudiencePerson) => (
            <div key={p.tg} data-audtg={p.tg}>
              <AudienceRow
                p={p}
                open={open === p.tg}
                sortKey={primarySort}
                detail={detail[p.tg]}
                busy={detailBusy === p.tg}
                selected={selected.has(p.tg)}
                onToggle={() => toggle(p)}
                onSelect={() => toggleSel(p.tg)}
                onMeta={(patch) => void saveMeta(p, patch)}
                onToast={onToast}
                onReloadDetail={reloadDetail}
              />
            </div>
          );
          const teamBlock = (t: { teamId: number; members: AudiencePerson[] }) => {
            const lg = t.members.reduce<AudiencePerson["lastGame"]>((best, m) => (m.lastGame && (!best || (m.lastGame.date + m.lastGame.time) > (best.date + best.time)) ? m.lastGame : best), null);
            return (
              <div key={`team-${t.teamId}`} className={css.audTeamBlock}>
                <div className={css.audTeamHead}>Команда · {t.members.length}{lg ? ` · ${lg.quest} ${fmtDate(lg.date)} ${lg.time}` : ""}</div>
                {t.members.map(row)}
              </div>
            );
          };
          if (b.kind === "solo") return row(b.p);
          if (b.kind === "team") return teamBlock(b);
          return (
            <div key={`rel-${b.relationId}`} className={css.audRelBlock}>
              <div className={css.audRelHead}>Связаны · {b.children.reduce((n, ch) => n + (ch.kind === "team" ? ch.members.length : 1), 0)}</div>
              {b.children.map((ch) => ch.kind === "team" ? teamBlock(ch) : row(ch.p))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AudienceRow({ p, open, sortKey, detail, busy, selected, onToggle, onSelect, onMeta, onToast, onReloadDetail }: {
  p: AudiencePerson;
  open: boolean;
  sortKey: SortKey;
  detail?: AudienceDetail;
  busy: boolean;
  selected: boolean;
  onToggle: () => void;
  onSelect: () => void;
  onMeta: (patch: AudienceMetaPatch) => void;
  onToast: (m: string) => void;
  onReloadDetail: (tg: number) => void;
}) {
  const activeTags = tagsOf(p);
  return (
    <article className={`${css.audRowCard} ${open ? css.audRowCardOpen : ""} ${selected ? css.audRowSel : ""} ${p.excluded ? css.audExcluded : ""} ${p.booked >= 1 ? css.audCaptainRow : ""} ${p.staff ? css.audStaffRow : ""}`}>
      <div style={{ display: "flex", alignItems: "stretch" }}>
        <AvatarButton p={p} selected={selected} onSelect={onSelect} />
        <button type="button" className={css.audRow} style={{ flex: 1, minWidth: 0 }} aria-expanded={open} onClick={onToggle}>
        <span className={css.audPerson}>
          <ConsentMark consent={p.consent} /><b>{p.name || "Без имени"}</b>
          {p.username ? <small>@{p.username}</small> : null}
          {p.staff ? <i>staff</i> : null}
          {activeTags.map((tag) => (
            <i key={tag} style={{ fontStyle: "normal", fontSize: 9.5, fontWeight: 800, padding: "1px 6px", borderRadius: 999, border: "1px solid var(--role)", color: "var(--role)", whiteSpace: "nowrap" }}>{tag}</i>
          ))}
          {p.sabotage ? <i style={{ fontStyle: "normal", fontSize: 9.5, fontWeight: 900, padding: "1px 6px", borderRadius: 999, background: "#c0392b", color: "#fff", whiteSpace: "nowrap", letterSpacing: "0.5px" }}>ХАОС</i> : null}
        </span>
        {p.blocked ? <span className={css.audBlocked} title="Заблокировал / недоступен для бота">⊘</span> : null}
        <span className={css.audInlineMetrics}>
          <b className={sortKey === "games" ? css.audMetricOn : ""} style={{ color: "var(--text-1)", fontSize: "1.1em" }}><span>ИГР</span> {money(p.games)}</b>
          <b className={sortKey === "money" ? css.audMetricOn : ""}>₽ {money(p.money)}</b>
        </span>
        <span className={css.audMarkers}>{p.excluded ? <i>искл.</i> : null}</span>
        <span className={css.audDate}>{fmtDate(p.linked_at)}</span>
        <span className={`${css.clChev} ${open ? css.clChevOpen : ""}`} aria-hidden="true">▾</span>
        </button>
      </div>
      {open ? (
        <div className={css.audDetail}>
          <div className={css.audStats}>
            <Stat label="коины" value={p.uc_balance} />
            <Stat label="заработал" value={p.uc_earned} />
            <Stat label="игр" value={p.games} />
            <Stat label="выручка" value={`${money(p.money)} ₽`} />
            <Stat label="привёл" value={p.invited} />
          </div>
          <div className={css.audMetaPanel}>
            <span>Теги</span>
            {TAGS.map((tag) => (
              <button key={tag.key} type="button" className={`${css.audTagBtn} ${tagOn(p, tag.key) ? css.audTagBtnOn : ""}`} onClick={() => onMeta({ [tag.key]: tagOn(p, tag.key) ? 0 : 1 })}>{tag.label}</button>
            ))}
            <button type="button" className={`${css.audTagBtn} ${p.excluded ? css.audTagDanger : ""}`} onClick={() => onMeta({ excluded: p.excluded ? 0 : 1 })}>
              {p.excluded ? "Вернуть" : "Исключить из списка"}
            </button>
            <button type="button" className={`${css.audTagBtn} ${p.sabotage ? css.audTagDanger : ""}`} onClick={() => onMeta({ sabotage: p.sabotage ? 0 : 1 })} title="Хаос-режим: взбешённая вёрстка + сильное замедление у этого человека">
              {p.sabotage ? "Выкл. хаос" : "Хаос-режим"}
            </button>
          </div>
          {busy && !detail ? <div className={css.ixLoading}>Загрузка деталей…</div> : null}
          {detail ? <AudienceDetailView d={detail} p={p} onToast={onToast} onReloadDetail={onReloadDetail} /> : null}
        </div>
      ) : null}
    </article>
  );
}

function AvatarButton({ p, selected, onSelect }: { p: AudiencePerson; selected: boolean; onSelect: () => void }) {
  const baseUrl = p.avatarUrl || "";
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const [visible, setVisible] = useState(false);
  const initial = (p.name || p.username || "?").trim().slice(0, 1).toUpperCase() || "?";

  // Грузим аватарку только когда строка попадает во вьюпорт: на длинном списке сотни
  // холодных запросов разом ратлимитят Telegram Bot API — отсюда «не грузятся».
  useEffect(() => {
    if (visible) return;
    const el = btnRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") { setVisible(true); return; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setVisible(true); io.disconnect(); }
    }, { rootMargin: "250px" });
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);

  // Загрузка через общий useAvatarSrc (глобальный лимит параллелизма + ретрай + 404),
  // но только когда строка во вьюпорте (visible) — иначе не грузим вовсе.
  const { src, failed } = useAvatarSrc(visible ? baseUrl : undefined);

  return (
    <button
      ref={btnRef}
      type="button"
      className={`${css.audAvatarBtn} ${selected ? css.audAvatarBtnOn : ""}`}
      aria-pressed={selected}
      title="Выделить для рассылки"
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
    >
      {src && !failed ? <img src={src} alt="" loading="lazy" /> : <span>{initial}</span>}
    </button>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return <span className={css.audStat}><small>{label}</small><b>{value}</b></span>;
}

function AudienceDetailView({ d, p, onToast, onReloadDetail }: { d: AudienceDetail; p: AudiencePerson; onToast: (m: string) => void; onReloadDetail: (tg: number) => void }) {
  const [attachOpen, setAttachOpen] = useState(false);
  const [openGame, setOpenGame] = useState<number | null>(null);
  const [gameComments, setGameComments] = useState<Record<number, AudienceGameComments>>({});
  const [gameCommentsBusy, setGameCommentsBusy] = useState<number | null>(null);
  const [gameCommentsError, setGameCommentsError] = useState<Record<number, string>>({});

  const toggleGame = useCallback((bookingId: number) => {
    setOpenGame((current) => current === bookingId ? null : bookingId);
    if (gameComments[bookingId] || gameCommentsBusy === bookingId) return;
    setGameCommentsBusy(bookingId);
    setGameCommentsError((prev) => ({ ...prev, [bookingId]: "" }));
    api<AudienceGameComments>(`/api/audience/booking/${bookingId}/comments`)
      .then((data) => setGameComments((prev) => ({ ...prev, [bookingId]: data })))
      .catch(() => setGameCommentsError((prev) => ({ ...prev, [bookingId]: "Не удалось загрузить комментарии" })))
      .finally(() => setGameCommentsBusy(null));
  }, [gameComments, gameCommentsBusy]);

  const renderGameComments = (bookingId: number) => {
    const data = gameComments[bookingId];
    const error = gameCommentsError[bookingId];
    if (gameCommentsBusy === bookingId && !data) return <div className={css.audCommentEmpty}>Загрузка комментариев…</div>;
    if (error) return <div className={css.audCommentEmpty}>{error}</div>;
    if (!data) return null;
    const rows: { key: string; label: string; text: string; warn?: boolean }[] = [];
    if (data.dcComment) rows.push({ key: "dc", label: "DC", text: data.dcComment });
    data.reports.forEach((report, idx) => {
      const operator = report.operator || "Оператор";
      if (report.comment) rows.push({ key: `op-${idx}`, label: report.isPrimary ? `${operator} · primary` : operator, text: report.comment });
      if (report.ooComment) rows.push({ key: `oo-${idx}`, label: "ОО", text: report.ooComment });
      if (report.needsCorrection) rows.push({ key: `fix-${idx}`, label: "Корректировка", text: report.correctionReason || "Требуется исправление", warn: true });
    });
    if (rows.length === 0) return <div className={css.audCommentEmpty}>Комментариев нет</div>;
    return (
      <div className={css.audGameComments}>
        {rows.map((row) => (
          <div key={row.key} className={`${css.audCommentLine} ${row.warn ? css.audCommentWarn : ""}`}>
            <span className={css.audCommentLabel}>{row.label}</span>
            <span>{row.text}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={css.audDeep}>
      <div>
        <h4>Юникоины — за что получил</h4>
        {d.earned.length === 0 ? <p>—</p> : d.earned.map((e) => <p key={e.type}><span>{typeLabel(e.type)} ×{e.n}</span><b>+{e.total}</b></p>)}
      </div>
      {d.spent.length > 0 ? (
        <div>
          <h4>Потратил</h4>
          {d.spent.map((e) => <p key={e.type}><span>{typeLabel(e.type)} ×{e.n}</span><b>{e.total}</b></p>)}
        </div>
      ) : null}
      {/* 06.10: «Связи» (откуда пришёл, кто выдал QR, кто привёл) и «Согласия» — DcPersonOrigin */}
      <DcPersonOrigin tg={p.tg} invitedBy={d.invitedBy} invitedPeople={d.invitedPeople} />
      <div>
        <h4>
          Последние игры
          <button type="button" onClick={() => setAttachOpen((v) => !v)}
            style={{ marginLeft: 8, padding: "2px 8px", borderRadius: 999, border: "1px solid var(--surface-3)", background: "transparent", color: "var(--role)", fontSize: 11, cursor: "pointer" }}>
            {attachOpen ? "скрыть" : "+ привязать игру"}
          </button>
        </h4>
        {/* Ручная привязка: номер в Telegram не совпал с номером в брони. */}
        {attachOpen ? <DcAttachGame tg={p.tg} onToast={onToast} onDone={() => { setAttachOpen(false); onReloadDetail(p.tg); }} /> : null}
        {d.games.length === 0 && !attachOpen ? <p style={{ fontSize: 12, color: "var(--text-3)" }}>Игр не найдено — номер в Telegram мог не совпасть с номером в брони.</p> : null}
      </div>
      {d.games.length > 0 ? (
        <div>
          <h4>Последние игры</h4>
          <div className={css.audGames}>
            {d.games.map((g) => {
              const isOpen = openGame === g.bookingId;
              return (
                <div key={g.bookingId} className={`${css.audGameItem} ${isOpen ? css.audGameItemOpen : ""}`}>
                  <button type="button" className={css.audGameButton} onClick={() => toggleGame(g.bookingId)} aria-expanded={isOpen}>
                    <span className={css.audGameTitle}>{g.quest}</span>
                    <span className={css.audGameMeta}>{fmtDate(g.date)} {g.time}</span>
                    <span className={`${css.audGameChev} ${isOpen ? css.audGameChevOpen : ""}`} aria-hidden="true">▾</span>
                  </button>
                  {isOpen ? renderGameComments(g.bookingId) : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
      <div className={css.audTech}>tg {p.tg}{p.client_id ? ` · клиент ${p.client_id}` : " · не привязан к брони"}{p.teamId ? ` · команда ${p.teamId}` : ""}</div>
    </div>
  );
}
